import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, X, Check } from "lucide-react";

const ADMIN_ID = 1; // ← Ganti dengan ID admin yang sebenarnya di database Anda

const Order = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [buyerId, setBuyerId] = useState(null);
  const [product, setProduct] = useState(null);
  const [buyerInfo, setBuyerInfo] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Get logged-in buyer ID
  useEffect(() => {
    const userStr = localStorage.getItem("erpUser") || localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      setBuyerId(user.userid || user.id);
    } else {
      setError("Anda harus login terlebih dahulu");
      setLoading(false);
    }
  }, []);

  // Fetch product + buyer + seller info
  useEffect(() => {
    if (!buyerId || !productId) return;

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);

        const prodRes = await axios.get(`https://api.kamulyaan.id/books/${productId}`);
        const prodData = prodRes.data;
        setProduct(prodData);

        const storeId = prodData?.storeid;

        const buyerRes = await axios.get(`https://api.kamulyaan.id/login/user/${buyerId}`);
        setBuyerInfo(buyerRes.data.user || buyerRes.data);

        if (storeId) {
          const sellerRes = await axios.get(`https://api.kamulyaan.id/seller/${storeId}`);
          setSellerInfo(sellerRes.data.seller || sellerRes.data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.response?.data?.message || err.message || "Gagal memuat data");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [buyerId, productId]);

  const totalAmount = product && quantity
    ? (parseFloat(product.price || 0) * parseInt(quantity || 1)).toFixed(2)
    : "0.00";

  const handleQuantityChange = (e) => {
    const val = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, val));
  };

  // Show confirmation modal
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  // Actual order creation + send notifications
  const confirmAndCreateOrder = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    setError(null);

    const orderData = {
      buyer_id: buyerId,
      store_id: product?.storeid,
      product_id: parseInt(productId),
      quantity: parseInt(quantity),
      price: parseFloat(product?.price || 0),
      total_amount: parseFloat(totalAmount),
      status: "Belum bayar",
    };

    try {
      // 1. Buat order
      const res = await axios.post("https://api.kamulyaan.id/order", orderData);

      if (res.data.success) {
        const createdOrderId = res.data.orderId;

        // 2. Kirim notifikasi ke buyer, seller, dan admin
        const notifyMessage = `Pesanan baru #${createdOrderId}\n` +
                             `Produk: ${product?.title || "—"} \n` +
                             `Jumlah: ${quantity} \n` +
                             `Total: Rp ${parseFloat(totalAmount).toLocaleString("id-ID")}`;

        // Notify buyer
        await axios.post("https://api.kamulyaan.id/msg", {
          user_id: buyerId,
          order_id: createdOrderId,
          title: "Pesanan Berhasil Dibuat",
          message: notifyMessage,
        });

        // Notify seller
        await axios.post("https://api.kamulyaan.id/msg", {
          user_id: product?.storeid,
          order_id: createdOrderId,
          title: "Ada Pesanan Baru",
          message: notifyMessage,
        });

        // Notify admin
        await axios.post("https://api.kamulyaan.id/msg", {
          user_id: 'heri',
          order_id: createdOrderId,
          title: "Pesanan Baru (Admin)",
          message: notifyMessage,
        });

        // 3. Tampilkan konfirmasi sukses
        alert(
          `Pesanan berhasil dibuat!\n\n` +
          `ID Pesanan: ${createdOrderId}\n` +
          `Produk: ${product?.title || "—"}\n` +
          `Jumlah: ${quantity}\n` +
          `Total: Rp ${parseFloat(totalAmount).toLocaleString("id-ID")}\n\n` +
          `Notifikasi telah dikirim ke Anda, penjual, dan admin.`
        );

        navigate("/orders"); // atau halaman daftar pesanan Anda
      } else {
        throw new Error(res.data.message || "Respons server tidak sukses");
      }
    } catch (err) {
      console.error("Submit error:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Gagal membuat pesanan. Coba lagi atau hubungi admin.";
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Memuat informasi pesanan...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-700 mb-3">Gagal Memuat</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-blue-600 text-white px-8 py-6">
          <h1 className="text-2xl font-bold">Buat Pesanan Baru</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Produk */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-8 border-b">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Produk</label>
              <div className="text-lg font-semibold">{product?.title || "—"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Harga Satuan</label>
              <div className="text-xl font-bold text-green-600">
                Rp {parseFloat(product?.price || 0).toLocaleString("id-ID")}
              </div>
            </div>
          </div>

          {/* Pembeli */}
          <div className="space-y-6 pb-8 border-b">
            <h3 className="text-lg font-semibold text-gray-800">Informasi Pembeli</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-600">Nama</label>
                <div className="mt-1 font-medium">{buyerInfo?.nama || buyerInfo?.userid || "—"}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Email</label>
                <div className="mt-1">{buyerInfo?.email || "—"}</div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600">Alamat</label>
                <div className="mt-1">{buyerInfo?.alamat || "—"}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Kota</label>
                <div className="mt-1">{buyerInfo?.kota || "—"}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Telepon / WA</label>
                <div className="mt-1 space-y-1">
                  {buyerInfo?.telpon && <div>Telp: {buyerInfo.telpon}</div>}
                  {buyerInfo?.wa && <div>WA: {buyerInfo.wa}</div>}
                  {!buyerInfo?.telpon && !buyerInfo?.wa && <div>—</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Penjual */}
          <div className="space-y-6 pb-8 border-b">
            <h3 className="text-lg font-semibold text-gray-800">Informasi Penjual</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-600">Store ID</label>
                <div className="mt-1 font-medium">{product?.storeid || "—"}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Nama / Toko</label>
                <div className="mt-1 font-medium">
                  {sellerInfo?.nama || sellerInfo?.userid || "—"}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Email Penjual</label>
                <div className="mt-1">{sellerInfo?.email || "—"}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Kontak Penjual</label>
                <div className="mt-1 space-y-1">
                  {sellerInfo?.telpon && <div>Telp: {sellerInfo.telpon}</div>}
                  {sellerInfo?.wa && <div>WA: {sellerInfo.wa}</div>}
                  {!sellerInfo?.telpon && !sellerInfo?.wa && <div>—</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Pesanan */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Detail Pesanan</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jumlah (Qty)
                </label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Harga Satuan
                </label>
                <div className="py-2.5 text-lg font-semibold text-green-600">
                  Rp {parseFloat(product?.price || 0).toLocaleString("id-ID")}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total
                </label>
                <div className="py-2.5 text-xl font-bold text-blue-700">
                  Rp {parseFloat(totalAmount).toLocaleString("id-ID")}
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-8 border-t">
            {error && <p className="text-red-600 text-center mb-6">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 px-6 rounded-xl font-bold text-white text-lg transition ${
                submitting ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {submitting ? "Memproses Pesanan..." : "Buat Pesanan"}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-xl font-semibold text-gray-900">Konfirmasi Pesanan</h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-3">
                <p className="font-medium text-gray-800">Detail pesanan:</p>
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 space-y-2.5">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Produk</span>
                    <span className="font-medium">{product?.title || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Jumlah</span>
                    <span className="font-medium">{quantity} pcs</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-xl font-bold text-green-700">
                      Rp {parseFloat(totalAmount).toLocaleString("id-ID")}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-amber-800 bg-amber-50 p-4 rounded-lg border border-amber-200">
                Status: Silakan lakukan pembayaran melalui WA atau QRIS ke admin dan informasikan agar pesanan segera diproses.
              </p>
            </div>

            <div className="flex justify-end gap-4 border-t px-6 py-5">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Batal
              </button>
              <button
                onClick={confirmAndCreateOrder}
                disabled={submitting}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white ${
                  submitting ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                }`}
              >
                <Check size={18} />
                {submitting ? "Memproses..." : "Ya, Buat Pesanan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Order;